import { api } from '../../../api/api';

export type ShareQrDecodeResponse = {
  success: boolean;
  uid: string;
  username: string;
  // Returned so the page can save/regenerate, but never rendered on screen.
  password: string;
  identifier: string;
  issued_at: string;
  expires_at: string;
  expired: boolean;
  validity_hours: number;
};

export type ShareQrRegenerateResponse = {
  success: boolean;
  uid: string;
  // data:image/png;base64,... — render directly and offer as a download
  qr_image: string;
  issued_at: string;
  expires_at: string;
  validity_hours: number;
};

export type ShareQrRegenerateRequest = {
  // camera_password is the camera's own credential; staff auth uses a session header.

  uid: string;
  camera_password: string;
  username?: string;
  identifier?: string;
};

export type SavedCamera = {
  id: number;
  uid: string;
  username: string;
  identifier: string;
  nickname: string;
  notes: string;
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
};

export const toolsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Team-only: upload a picture of a CamHi/Farmstream share QR and get the
    // camera UID + credentials back. Body is FormData (image only) so
    // the browser sets the multipart boundary itself.
    decodeShareQr: build.mutation<ShareQrDecodeResponse, FormData>({
      query: (formData) => ({
        method: 'POST',
        url: 'tools/decode-share-qr',
        body: formData
      })
    }),
    // Team-only: mint a fresh 24h share QR from just-decoded credentials.
    regenerateShareQr: build.mutation<ShareQrRegenerateResponse, ShareQrRegenerateRequest>({
      query: (body) => ({
        method: 'POST',
        url: 'tools/regenerate-share-qr',
        body
      })
    }),
    // Save (upsert by UID) a decoded camera with nickname + notes. The
    // password is encrypted at rest and never comes back out.
    saveCamera: build.mutation<
      { success: boolean; updated: boolean; camera: SavedCamera },
      {

        uid: string;
        camera_password: string;
        username?: string;
        identifier?: string;
        nickname: string;
        notes?: string;
      }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'tools/cameras/save',
        body
      })
    }),
    listCameras: build.mutation<
      { success: boolean; cameras: SavedCamera[] },
      {  search?: string }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'tools/cameras/list',
        body
      })
    }),
    updateCamera: build.mutation<
      { success: boolean; camera: SavedCamera },
      {  id: number; nickname: string; notes: string }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'tools/cameras/update',
        body
      })
    }),
    deleteCamera: build.mutation<{ success: boolean }, {  id: number }>({
      query: (body) => ({
        method: 'POST',
        url: 'tools/cameras/delete',
        body
      })
    }),
    // Decrypt and return a saved camera's password for on-screen viewing.
    revealCameraPassword: build.mutation<
      { success: boolean; uid: string; username: string; password: string },
      {  id: number }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'tools/cameras/password',
        body
      })
    }),
    // Fresh share QR for a saved camera — the stored password is decrypted
    // server-side and returned inside the QR image.
    savedCameraShareQr: build.mutation<ShareQrRegenerateResponse, {  id: number }>({
      query: (body) => ({
        method: 'POST',
        url: 'tools/cameras/share-qr',
        body
      })
    })
  })
});

export const {
  useDecodeShareQrMutation,
  useRegenerateShareQrMutation,
  useSaveCameraMutation,
  useListCamerasMutation,
  useUpdateCameraMutation,
  useDeleteCameraMutation,
  useRevealCameraPasswordMutation,
  useSavedCameraShareQrMutation
} = toolsApi;
